/**
 * User Settings Page
 *
 * Security Design (for viva):
 * - This page manages PREFERENCES and ACCOUNT INFO only
 * - NO encryption key management — keys never leave the client
 * - NO algorithm selection — AES-256-GCM is the system standard
 * - NO password sharing or file access controls (handled elsewhere)
 * - Session timeout is a security preference that limits exposure
 * - All settings are stored per-user in localStorage (client-side)
 * - Display name is cosmetic only — does not affect authentication
 *
 * Why certain settings are NOT here:
 * - Encryption keys: derived from password, never stored
 * - Algorithm choice: fixed at AES-256-GCM for security consistency
 * - Admin features: role-based separation prevents unauthorized access
 */

import React, { useState, useEffect } from 'react';
import { getUserStats, getPendingAccessCount } from '../services/api';
import { formatDateTime } from '../utils/time';

// Settings are stored per-user in localStorage
function loadUserSettings(email) {
  try {
    const key = email ? `cdeaas_settings_${email}` : 'cdeaas_settings';
    const saved = JSON.parse(localStorage.getItem(key) || '{}');
    return { darkMode: true, sessionTimeout: 15, itemsPerPage: 15, dateFormat: '24h', displayName: '', notifications: true, exportFormat: 'pdf', compactMode: false, ...saved };
  } catch { return { darkMode: true, sessionTimeout: 15, itemsPerPage: 15, dateFormat: '24h', displayName: '', notifications: true, exportFormat: 'pdf', compactMode: false }; }
}

function saveUserSettings(email, settings) {
  const key = email ? `cdeaas_settings_${email}` : 'cdeaas_settings';
  localStorage.setItem(key, JSON.stringify(settings));
  window.dispatchEvent(new Event('settingsChanged'));
}

function UserSettingsPage({ user }) {
  const [cfg, setCfg] = useState(() => loadUserSettings(user?.email));
  const [nameEdit, setNameEdit] = useState(cfg.displayName || '');
  const [saved, setSaved] = useState(false);
  const [stats, setStats] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    getUserStats().then(setStats).catch(() => {});
    getPendingAccessCount().then(d => setPendingCount(d?.count || 0)).catch(() => {});
  }, []);

  const update = (key, value) => {
    const next = { ...cfg, [key]: value };
    setCfg(next);
    saveUserSettings(user?.email, next);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const Toggle = ({ on, onChange }) => (
    <button onClick={() => onChange(!on)} style={{
      width: 44, height: 24, borderRadius: 14, border: 'none', cursor: 'pointer',
      background: on ? 'var(--blue)' : 'var(--gray-300)', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', top: 2, left: on ? 22 : 2, width: 20, height: 20,
        borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </button>
  );

  return (
    <div className="page-container admin-page-shell admin-tactile-page">
      <h2 className="user-premium-page-title" style={{ fontSize: 22, fontWeight: 900, margin: '0 0 6px', letterSpacing: '-0.4px' }}>User Settings</h2>
      <p style={st.subtitle}>Manage your account preferences and view security information.</p>

      {/* ── Profile ── */}
      <div style={st.card}>
        <div style={st.sTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          Profile
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Email Address</div><div style={st.sub}>Your login identity — cannot be changed</div></div>
          <span style={st.val}>{user?.email}</span>
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Role</div><div style={st.sub}>Assigned by system administrator</div></div>
          <span className="security-badge green">USER</span>
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Account Created</div><div style={st.sub}>When your account was registered</div></div>
          <span style={st.val}>{user?.created_at ? formatDateTime(user.created_at) : '—'}</span>
        </div>
        <div style={st.rowLast}>
          <div><div style={st.lbl}>Display Name</div><div style={st.sub}>Cosmetic only — does not affect authentication</div></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input style={st.input} value={nameEdit}
              onChange={e => setNameEdit(e.target.value)}
              onBlur={() => { if (nameEdit !== cfg.displayName) update('displayName', nameEdit); }}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
              placeholder="Enter your name" />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Auto-saved</span>
          </div>
        </div>
      </div>

      {/* ── Security Status ──
        Why this section exists: Users should see their security posture
        without accessing audit logs or system-level data.
        All items here are READ-ONLY status indicators. */}
      <div style={st.card}>
        <div style={st.sTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Security Status
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Two-Step Verification (OTP)</div><div style={st.sub}>Required before any file decryption</div></div>
          <span className="security-badge green">ENABLED</span>
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Encryption Algorithm</div><div style={st.sub}>System-wide standard — cannot be changed per-user</div></div>
          <span className="security-badge green">AES-256-GCM</span>
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Key Derivation</div><div style={st.sub}>PBKDF2 with high iteration count</div></div>
          <span style={st.val}>100,000 iterations</span>
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Zero-Knowledge Architecture</div><div style={st.sub}>Server never sees your plaintext or encryption keys</div></div>
          <span className="security-badge green">ACTIVE</span>
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Session Timeout</div><div style={st.sub}>Auto-logout after period of inactivity</div></div>
          <select style={st.select} value={cfg.sessionTimeout} onChange={e => update('sessionTimeout', Number(e.target.value))}>
            <option value={5}>5 minutes</option>
            <option value={10}>10 minutes</option>
            <option value={15}>15 minutes (default)</option>
            <option value={30}>30 minutes</option>
            <option value={60}>1 hour</option>
          </select>
        </div>
        <div style={st.rowLast}>
          <div><div style={st.lbl}>Files Encrypted</div><div style={st.sub}>Total files you have encrypted on this platform</div></div>
          <span style={st.val}>{stats?.total_files ?? '—'}</span>
        </div>
      </div>

      {/* Security Limitation Notice */}
      <div style={st.secNotice}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
        <div>
          <strong>Why some settings are not configurable:</strong> The encryption algorithm (AES-256-GCM), key derivation method (PBKDF2), and OTP requirement are system-wide security standards. Allowing per-user changes could weaken the overall security model. This is by design for a zero-knowledge architecture.
        </div>
      </div>

      {/* ── Appearance ── */}
      <div style={st.card}>
        <div style={st.sTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
          Appearance
        </div>
        <div style={st.rowLast}>
          <div><div style={st.lbl}>Dark Mode</div><div style={st.sub}>Switch between light and dark theme</div></div>
          <Toggle on={cfg.darkMode} onChange={v => update('darkMode', v)} />
        </div>
      </div>

      {/* ── Notification Preferences ── */}
      <div style={st.card}>
        <div style={st.sTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--yellow)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          Notifications
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Enable Notifications</div><div style={st.sub}>Show badges for access requests and alerts</div></div>
          <Toggle on={cfg.notifications} onChange={v => update('notifications', v)} />
        </div>
        <div style={st.rowLast}>
          <div><div style={st.lbl}>Pending Access Requests</div><div style={st.sub}>Requests waiting for your approval</div></div>
          <span style={{ ...st.val, color: pendingCount > 0 ? 'var(--red)' : 'var(--green)' }}>{pendingCount}</span>
        </div>
      </div>

      {/* ── Preferences ── */}
      <div style={st.card}>
        <div style={st.sTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
          Display Preferences
        </div>
        <div style={st.row}>
          <div><div style={st.lbl}>Date/Time Format</div><div style={st.sub}>How timestamps are displayed</div></div>
          <select style={st.select} value={cfg.dateFormat} onChange={e => update('dateFormat', e.target.value)}>
            <option value="24h">24-hour (14:30)</option>
            <option value="12h">12-hour (2:30 PM)</option>
          </select>
        </div>
        <div style={st.rowLast}>
          <div><div style={st.lbl}>Default Export Format</div><div style={st.sub}>Preferred format for file reports</div></div>
          <select style={st.select} value={cfg.exportFormat} onChange={e => update('exportFormat', e.target.value)}>
            <option value="pdf">PDF Document</option>
            <option value="excel">Excel Spreadsheet</option>
          </select>
        </div>
      </div>

      {/* ── System Info ── */}
      <div style={st.card}>
        <div style={st.sTitle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          System Information
        </div>
        <div style={st.row}><div style={st.lbl}>Platform</div><span style={st.val}>JOSMY v1.0</span></div>
        <div style={st.row}><div style={st.lbl}>Architecture</div><span style={st.val}>Zero-Knowledge Encryption</span></div>
        <div style={st.rowLast}><div style={st.lbl}>Backend</div><span style={st.val}>FastAPI + SQL Database</span></div>
      </div>

      {saved && (
        <div style={st.toast}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          Settings saved
        </div>
      )}
    </div>
  );
}

const st = {
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 20px', fontWeight: 500 },
  card: {
    background: 'var(--surface-85)', borderRadius: 22, border: '1.5px solid rgba(99,102,241,0.08)',
    padding: 28, marginBottom: 20, boxShadow: '0 4px 20px rgba(99,102,241,0.06)',
    backdropFilter: 'blur(12px)',
  },
  sTitle: { fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10, letterSpacing: '-0.2px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid rgba(99,102,241,0.06)', gap: 16, flexWrap: 'wrap' },
  rowLast: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', gap: 16, flexWrap: 'wrap' },
  lbl: { fontSize: 13, fontWeight: 700, color: 'var(--text-body)' },
  sub: { fontSize: 11, color: 'var(--text-muted)', marginTop: 3, fontWeight: 500 },
  val: { fontSize: 14, color: 'var(--text-primary)', fontWeight: 600 },
  select: { padding: '10px 14px', border: '1.5px solid rgba(99,102,241,0.1)', borderRadius: 14, fontSize: 14, backgroundColor: 'var(--surface-90)', color: 'var(--text-body)', cursor: 'pointer', minWidth: 150, transition: 'all 0.2s ease' },
  input: { padding: '10px 14px', border: '1.5px solid rgba(99,102,241,0.1)', borderRadius: 14, fontSize: 14, backgroundColor: 'var(--surface-90)', color: 'var(--text-body)', width: 210, transition: 'all 0.2s ease' },
  secNotice: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 20px', background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.03))', border: '1.5px solid rgba(99,102,241,0.1)', borderRadius: 18, marginBottom: 20, fontSize: 13, color: 'var(--text-faint)', lineHeight: 1.5 },
  toast: { position: 'fixed', bottom: 24, right: 24, background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', padding: '12px 22px', borderRadius: 14, fontSize: 13, fontWeight: 700, boxShadow: '0 6px 20px rgba(34,197,94,0.3)', zIndex: 999, display: 'flex', alignItems: 'center', gap: 8 },
};

export default UserSettingsPage;
